import React, { useState, useEffect, useMemo } from 'react';

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

export const Logs = () => {
    const [entries, setEntries] = useState(null);
    const [error, setError] = useState(null);
    const [selectedApp, setSelectedApp] = useState(null);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileBody, setFileBody] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);
    const [fileError, setFileError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch('api/logs', { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => { if (!cancelled) setEntries(data); })
            .catch(err => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, []);

    const apps = useMemo(() => {
        if (!entries) return [];
        return Array.from(new Set(entries.map(e => e.application))).sort();
    }, [entries]);

    const machines = useMemo(() => {
        if (!entries || !selectedApp) return [];
        return Array.from(new Set(
            entries.filter(e => e.application === selectedApp).map(e => e.machine)
        )).sort();
    }, [entries, selectedApp]);

    const files = useMemo(() => {
        if (!entries || !selectedApp || !selectedMachine) return [];
        return entries
            .filter(e => e.application === selectedApp && e.machine === selectedMachine)
            .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
    }, [entries, selectedApp, selectedMachine]);

    useEffect(() => {
        if (!selectedFile) {
            setFileBody(null);
            setFileError(null);
            return;
        }
        let cancelled = false;
        setFileLoading(true);
        setFileError(null);
        const url = `api/logs/file?application=${encodeURIComponent(selectedFile.application)}`
            + `&machine=${encodeURIComponent(selectedFile.machine)}`
            + `&file=${encodeURIComponent(selectedFile.file)}`;
        fetch(url, { credentials: 'include' })
            .then(r => r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(text => { if (!cancelled) { setFileBody(text); setFileLoading(false); } })
            .catch(err => { if (!cancelled) { setFileError(err.message); setFileLoading(false); } });
        return () => { cancelled = true; };
    }, [selectedFile]);

    const pickApp = (app) => {
        setSelectedApp(app);
        setSelectedMachine(null);
        setSelectedFile(null);
    };
    const pickMachine = (machine) => {
        setSelectedMachine(machine);
        setSelectedFile(null);
    };

    return (
        <div className="container-fluid py-3" style={{ height: 'calc(100vh - 80px)' }}>
            <div className="d-flex align-items-baseline mb-3">
                <h2 className="mb-0">Diagnostic Logs</h2>
                <span className="text-muted ms-3 small">
                    {entries ? `${entries.length} files` : ''}
                </span>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    Failed to load log list: {error}
                </div>
            )}

            {!entries && !error && <p><em>Loading…</em></p>}

            {entries && (
                <div className="row g-2" style={{ height: 'calc(100% - 60px)' }}>
                    <div className="col-md-2 d-flex flex-column" style={{ minHeight: 0 }}>
                        <div className="card flex-grow-1" style={{ overflow: 'hidden' }}>
                            <div className="card-header py-1 small fw-bold">Application</div>
                            <div className="list-group list-group-flush" style={{ overflowY: 'auto' }}>
                                {apps.map(app => (
                                    <button
                                        key={app}
                                        type="button"
                                        className={`list-group-item list-group-item-action small ${selectedApp === app ? 'active' : ''}`}
                                        onClick={() => pickApp(app)}
                                    >
                                        {app}
                                    </button>
                                ))}
                                {apps.length === 0 && (
                                    <div className="list-group-item small text-muted">No logs uploaded.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2 d-flex flex-column" style={{ minHeight: 0 }}>
                        <div className="card flex-grow-1" style={{ overflow: 'hidden' }}>
                            <div className="card-header py-1 small fw-bold">Machine</div>
                            <div className="list-group list-group-flush" style={{ overflowY: 'auto' }}>
                                {machines.map(m => (
                                    <button
                                        key={m}
                                        type="button"
                                        className={`list-group-item list-group-item-action small ${selectedMachine === m ? 'active' : ''}`}
                                        onClick={() => pickMachine(m)}
                                    >
                                        {m}
                                    </button>
                                ))}
                                {selectedApp && machines.length === 0 && (
                                    <div className="list-group-item small text-muted">No machines.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-3 d-flex flex-column" style={{ minHeight: 0 }}>
                        <div className="card flex-grow-1" style={{ overflow: 'hidden' }}>
                            <div className="card-header py-1 small fw-bold">File</div>
                            <div className="list-group list-group-flush" style={{ overflowY: 'auto' }}>
                                {files.map(f => (
                                    <button
                                        key={f.key}
                                        type="button"
                                        className={`list-group-item list-group-item-action small ${selectedFile && selectedFile.key === f.key ? 'active' : ''}`}
                                        onClick={() => setSelectedFile(f)}
                                    >
                                        <div className="text-truncate">{f.file}</div>
                                        <div className="d-flex justify-content-between" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
                                            <span>{formatDate(f.lastModified)}</span>
                                            <span>{formatSize(f.size)}</span>
                                        </div>
                                    </button>
                                ))}
                                {selectedMachine && files.length === 0 && (
                                    <div className="list-group-item small text-muted">No files.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-5 d-flex flex-column" style={{ minHeight: 0 }}>
                        <div className="card flex-grow-1" style={{ overflow: 'hidden' }}>
                            <div className="card-header py-1 small fw-bold d-flex justify-content-between">
                                <span>{selectedFile ? selectedFile.file : 'Select a file'}</span>
                                {selectedFile && (
                                    <span className="text-muted">{formatSize(selectedFile.size)}</span>
                                )}
                            </div>
                            <div style={{ overflow: 'auto', flexGrow: 1, height: '100%', backgroundColor: '#1e1e1e' }}>
                                {fileLoading && <div className="text-light p-3"><em>Loading…</em></div>}
                                {fileError && <div className="alert alert-danger m-3">{fileError}</div>}
                                {fileBody !== null && !fileLoading && (
                                    <pre className="text-light p-3 mb-0" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {fileBody}
                                    </pre>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
